import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { RecipeV2Detail } from '../../../app/services/api/recipeV2';
import { buildMatrixColumns, buildMatrixRecipeProvenance } from '../publishing/recipePublishing';

interface GovernedMatrixPreviewProps {
  recipes: RecipeV2Detail[];
}

const markerLabel = (marker: string): string => {
  switch (marker) {
    case 'C': return 'Contains';
    case 'M': return 'May contain';
    case 'F': return 'Free from';
    case '-': return 'Not present';
    case '?': return 'Unknown';
    case '*': return 'Certified';
    case 'Y': return 'Suitable';
    case 'N': return 'Not suitable';
    default: return marker;
  }
};

const GovernedMatrixPreview = ({ recipes }: GovernedMatrixPreviewProps) => {
  const columns = buildMatrixColumns(recipes);

  if (!recipes.length) {
    return (
      <Typography color="text.secondary">
        No governed recipes are available for this matrix.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="textSM"
        color="text.secondary">
        This preview uses the same governed recipe, publication and effective compliance snapshots as the downloadable matrix.
      </Typography>

      <TableContainer sx={{ maxHeight: 520, border: 1, borderColor: 'divider' }}>
        <Table stickyHeader
          size="small"
          aria-label="Governed allergen and dietary matrix">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 260 }}>Recipe</TableCell>
              {columns.map((column) => (
                <TableCell key={column.key}
                  align="center"
                  sx={{ minWidth: 92 }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {recipes.map((recipe) => (
              <TableRow key={recipe.id}
                hover>
                <TableCell>
                  <Stack spacing={0.5}>
                    <Typography variant="textSM"
                      fontWeight={600}>
                      {recipe.description}
                    </Typography>
                    <Typography variant="textXS"
                      color="text.secondary">
                      {buildMatrixRecipeProvenance(recipe)}
                    </Typography>
                  </Stack>
                </TableCell>
                {columns.map((column) => {
                  const marker = column.marker(recipe);
                  return (
                    <TableCell key={column.key}
                      align="center">
                      <Chip
                        size="small"
                        label={markerLabel(marker)}
                        variant="outlined"
                        sx={{ minWidth: 72 }}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box>
        <Typography variant="textXS"
          color="text.secondary">
          Allergen states: Contains, May contain, Free from, Not present and Unknown. Dietary states: Certified, Suitable, Not suitable, Contains, May contain, Free from and Unknown. Missing data remains Unknown.
        </Typography>
      </Box>
    </Stack>
  );
};

export default GovernedMatrixPreview;
